import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { DeviceType } from '../../pages/admin/devices/deviceTypes';
import { formatDeviceTypeLabel } from '../../pages/admin/devices/deviceTypes';
import { inferAllowedDeviceTypesForChecklistRow } from '../../lib/checklistDeviceFilter';

export type ChecklistPickerDeviceRow = {
    _id: string;
    type?: string;
    installationLocation?: string;
    floor?: string;
    zone?: string;
    placement?: string;
    manufacturer?: string;
    modelName?: string;
    serialNumber?: string;
};

type Props = {
    categoryLabel: string;
    itemLabel: string;
    devices: ChecklistPickerDeviceRow[];
    /** Current linked device object id when known */
    valueId: string | null | undefined;
    onCommit: (deviceId: string | null) => void;
    saving?: boolean;
    disabledReason?: string | null;
    /** Override inferred device-type filter (field-device sections). */
    allowedTypes?: import('../../pages/admin/devices/deviceTypes').DeviceType[] | null;
    /** Hide devices already logged for this section. */
    excludeDeviceIds?: string[];
    /** Optional label override for the picker trigger. */
    triggerLabel?: string;
};

function deviceMatchesSearch(d: ChecklistPickerDeviceRow, q: string): boolean {
    const s = `${formatDeviceTypeLabel(d.type)} ${d.installationLocation || ''} ${d.manufacturer || ''} ${d.modelName || ''} ${d.serialNumber || ''}`.toLowerCase();
    const words = q.toLowerCase().trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return true;
    return words.every((w) => s.includes(w));
}

export default function ChecklistDevicePicker(props: Props): React.JSX.Element {
    const {
        categoryLabel,
        itemLabel,
        devices,
        valueId,
        onCommit,
        saving,
        disabledReason,
        allowedTypes,
        excludeDeviceIds,
        triggerLabel
    } = props;
    const inferred = useMemo(() => inferAllowedDeviceTypesForChecklistRow(categoryLabel, itemLabel), [categoryLabel, itemLabel]);
    const excludeSet = useMemo(() => new Set((excludeDeviceIds || []).map(String)), [excludeDeviceIds]);

    const hasExplicitAllowed = Array.isArray(allowedTypes);

    const filteredByType = useMemo(() => {
        const rows = devices.filter((d) => !excludeSet.has(d._id));
        if (hasExplicitAllowed) {
            if (allowedTypes.length === 0) return [];
            const set = new Set<string>(allowedTypes);
            return rows.filter((d) => d.type && set.has(d.type as DeviceType));
        }
        if (!inferred || inferred.length === 0) return rows;
        const set = new Set<string>(inferred);
        return rows.filter((d) => d.type && set.has(d.type as DeviceType));
    }, [devices, allowedTypes, hasExplicitAllowed, inferred, excludeSet]);

    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const list = useMemo(
        () => filteredByType.filter((d) => deviceMatchesSearch(d, query)),
        [filteredByType, query]
    );

    const rootRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);

    const selectedRow = devices.find((d) => d._id === valueId);

    useEffect(() => {
        if (!open) setQuery('');
    }, [open]);

    const activeTypes = hasExplicitAllowed ? allowedTypes : inferred;
    const hintSimple =
        hasExplicitAllowed
            ? allowedTypes.length > 0
                ? `Showing only: ${allowedTypes.map((t) => formatDeviceTypeLabel(t)).join(', ')}.`
                : 'No device types match this section.'
            : activeTypes && activeTypes.length > 0
              ? `Showing only: ${activeTypes.map((t) => formatDeviceTypeLabel(t)).join(', ')}.`
              : 'Showing all devices for this building.';

    return (
        <div ref={rootRef} className="ir-device-picker" style={{ marginTop: 8, position: 'relative' }}>
            <label className="form-label" style={{ fontSize: '0.78rem', display: 'block', marginBottom: 4 }}>
                {triggerLabel || 'Device on this checklist line'}
                {disabledReason ? <span style={{ color: '#b45309' }}>{` · ${disabledReason}`}</span> : null}
            </label>
            <button
                type="button"
                disabled={!!disabledReason || saving}
                className="ir-device-picker__trigger"
                onClick={() => setOpen((o) => !o)}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                {selectedRow ?
                    `${formatDeviceTypeLabel(selectedRow.type)} · ${selectedRow.installationLocation || selectedRow.serialNumber || selectedRow._id.slice(-6)}`
                :   triggerLabel ? 'Select device to inspect…' : 'Choose device…'}
            </button>
            {selectedRow ?
                <button
                    type="button"
                    disabled={!!disabledReason || saving}
                    className="dash-btn dash-btn-ghost"
                    style={{ marginTop: 6, fontSize: '0.76rem', padding: '4px 10px' }}
                    onClick={() => void onCommit(null)}
                >
                    Clear link
                </button>
            : null}

            {open ?
                (
                    <div
                        role="listbox"
                        style={{
                            position: 'absolute',
                            zIndex: 30,
                            left: 0,
                            right: 0,
                            top: '100%',
                            marginTop: 6,
                            background: '#fff',
                            borderRadius: 10,
                            boxShadow: '0 12px 32px rgba(0,0,0,0.14)',
                            border: '1px solid rgba(0,0,0,0.1)',
                            maxHeight: 280,
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        <div style={{ padding: 10, borderBottom: '1px solid rgba(0,0,0,0.06)', fontSize: '0.75rem', color: 'var(--dash-muted)', lineHeight: 1.4 }}>
                            {hintSimple}{' '}
                            <span style={{ opacity: 0.85 }}>
                                Search filters the list further.
                            </span>
                        </div>
                        <input
                            autoFocus
                            placeholder="Search location, SN, model…"
                            className="form-input"
                            style={{ display: 'block', width: 'auto', margin: '8px 10px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                        />
                        <div style={{ overflowY: 'auto', flex: 1 }}>
                            {list.length === 0 ?
                                <div style={{ padding: '14px 12px', color: 'var(--dash-muted)', fontSize: '0.82rem' }}>No matching devices.</div>
                            : list.map((d) => (
                                <button
                                    key={d._id}
                                    type="button"
                                    role="option"
                                    aria-selected={d._id === valueId}
                                    onClick={() => {
                                        void onCommit(d._id);
                                        setOpen(false);
                                    }}
                                    style={{
                                        width: '100%',
                                        padding: '10px 14px',
                                        border: 'none',
                                        borderBottom: '1px solid rgba(0,0,0,0.05)',
                                        textAlign: 'left',
                                        background: d._id === valueId ? 'rgba(15,118,110,0.08)' : 'transparent',
                                        cursor: 'pointer',
                                        fontSize: '0.82rem'
                                    }}
                                >
                                    <div style={{ fontWeight: 800 }}>{formatDeviceTypeLabel(d.type)}</div>
                                    <div style={{ color: 'var(--dash-muted)', marginTop: 2 }}>
                                        {[d.installationLocation, d.floor, d.zone].filter(Boolean).join(' · ') || '—'}
                                    </div>
                                    <div style={{ color: 'var(--dash-muted)', marginTop: 2, fontSize: '0.75rem' }}>
                                        {[d.manufacturer, d.modelName].filter(Boolean).join(' · ')}
                                        {d.serialNumber ? ` · SN ${d.serialNumber}` : ''}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                )
            : null}
        </div>
    );
}
